import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LocalOffer as LocalOfferIcon,
  Image as ImageIcon,
  BugReport as BugReportIcon,
  Close as CloseIcon,
  AutoFixHigh as AutoFixHighIcon
} from '@mui/icons-material';
import {
  getAllPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion
} from '../../services/promotionService';
import {
  Promotion,
  CreatePromotionData,
  UpdatePromotionData,
  PromotionStatus,
  DiscountType,
  PromotionScope,
  QuantityCondition
} from '../../types/promotion';
import { InventoryItem, Category } from '../../utils/types';
import { getAllInventoryItems } from '../../services/inventoryService';
import { getAllCategories } from '../../services/categoryService';
import PromotionDiagnosticsComponent from '../../components/admin/PromotionDiagnostics';
import PromotionQuickFix from '../../components/admin/PromotionQuickFix';
// تم إزالة استيراد مكتبات DatePicker

// واجهة خصائص TabPanel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// مكون TabPanel
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`promotion-tabpanel-${index}`}
      aria-labelledby={`promotion-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// نموذج بيانات النموذج
interface FormData extends CreatePromotionData {
  selectedProductIds: string[];
  selectedCategoryIds: string[];
  quantityConditions: QuantityCondition[];
}

const PromotionsManagementPage: React.FC = () => {
  // حالة الصفحة
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentPromotion, setCurrentPromotion] = useState<Promotion | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showQuickFix, setShowQuickFix] = useState(false);
  const [products, setProducts] = useState<InventoryItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // بيانات النموذج
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    imageUrl: '',
    startDate: new Date(),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
    status: PromotionStatus.ACTIVE,
    scope: PromotionScope.PRODUCT,
    discountType: DiscountType.PERCENTAGE,
    discountValue: {
      value: 10,
      maxDiscount: undefined
    },
    conditions: {
      productIds: [],
      categoryIds: [],
      minOrderAmount: undefined,
      quantityConditions: []
    },
    buyXGetYDetails: {
      buyQuantity: 1,
      getQuantity: 1
    },
    bundleDetails: {
      productIds: [],
      bundlePrice: 0
    },
    usageLimit: undefined,
    priority: 1,
    isActive: true,
    showInOffers: true,
    selectedProductIds: [],
    selectedCategoryIds: [],
    quantityConditions: []
  });

  // تحميل البيانات عند تحميل الصفحة
  useEffect(() => {
    loadPromotions();
    loadProducts();
    loadCategories();
  }, []);

  // تحميل العروض
  const loadPromotions = async () => {
    try {
      setLoading(true);
      console.log('🔄 بدء تحميل العروض...');

      const promotionsData = await getAllPromotions();
      setPromotions(promotionsData);

      console.log(`✅ تم تحميل ${promotionsData.length} عرض بنجاح`);

      if (promotionsData.length === 0) {
        setSnackbar({
          open: true,
          message: 'لا توجد عروض حالياً. يمكنك إضافة عرض جديد.',
          severity: 'info'
        });
      }

    } catch (error) {
      console.error('❌ خطأ في تحميل العروض:', error);

      let errorMessage = 'حدث خطأ أثناء تحميل العروض';

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });

      // في حالة الخطأ، تعيين قائمة فارغة
      setPromotions([]);
    } finally {
      setLoading(false);
    }
  };

  // تحميل المنتجات
  const loadProducts = async () => {
    try {
      const productsData = await getAllInventoryItems();
      // تصفية المنتجات المعروضة في المتجر فقط
      const storeProducts = productsData.filter(product => product.showInStore);
      setProducts(storeProducts);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  // تحميل الفئات
  const loadCategories = async () => {
    try {
      const categoriesData = await getAllCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // تغيير التبويب
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // التحقق من صحة البيانات
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'عنوان العرض مطلوب';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'وصف العرض مطلوب';
    }

    if (formData.discountValue.value <= 0) {
      newErrors.discountValue = 'قيمة التخفيض يجب أن تكون أكبر من صفر';
    }

    if (formData.discountType === DiscountType.PERCENTAGE && formData.discountValue.value > 100) {
      newErrors.discountValue = 'نسبة التخفيض يجب أن تكون بين 1 و 100';
    }

    if (formData.scope === PromotionScope.PRODUCT && formData.selectedProductIds.length === 0) {
      newErrors.selectedProductIds = 'يجب اختيار منتج واحد على الأقل';
    }

    if (formData.scope === PromotionScope.CATEGORY && formData.selectedCategoryIds.length === 0) {
      newErrors.selectedCategoryIds = 'يجب اختيار فئة واحدة على الأقل';
    }

    if (formData.discountType === DiscountType.BUY_X_GET_Y) {
      if (!formData.buyXGetYDetails || formData.buyXGetYDetails.buyQuantity <= 0) {
        newErrors.buyQuantity = 'كمية الشراء يجب أن تكون أكبر من صفر';
      }
      if (!formData.buyXGetYDetails || formData.buyXGetYDetails.getQuantity <= 0) {
        newErrors.getQuantity = 'كمية الهدية يجب أن تكون أكبر من صفر';
      }
    }

    if (formData.discountType === DiscountType.BUNDLE) {
      if (!formData.bundleDetails || formData.bundleDetails.productIds.length < 2) {
        newErrors.bundleProductIds = 'يجب اختيار منتجين على الأقل للحزمة';
      }
      if (!formData.bundleDetails || formData.bundleDetails.bundlePrice <= 0) {
        newErrors.bundlePrice = 'سعر الحزمة يجب أن يكون أكبر من صفر';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // فتح نافذة إضافة عرض جديد
  const handleAddPromotion = () => {
    setCurrentPromotion(null);
    setFormData({
      title: '',
      description: '',
      imageUrl: '',
      startDate: new Date(),
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
      status: PromotionStatus.ACTIVE,
      scope: PromotionScope.PRODUCT,
      discountType: DiscountType.PERCENTAGE,
      discountValue: {
        value: 10,
        maxDiscount: undefined
      },
      conditions: {
        productIds: [],
        categoryIds: [],
        minOrderAmount: undefined,
        quantityConditions: []
      },
      buyXGetYDetails: {
        buyQuantity: 1,
        getQuantity: 1
      },
      bundleDetails: {
        productIds: [],
        bundlePrice: 0
      },
      usageLimit: undefined,
      priority: 1,
      isActive: true,
      showInOffers: true,
      selectedProductIds: [],
      selectedCategoryIds: [],
      quantityConditions: []
    });
    setErrors({});
    setTabValue(0);
    setOpenDialog(true);
  };

  // فتح نافذة تعديل عرض
  const handleEditPromotion = (promotion: Promotion) => {
    setCurrentPromotion(promotion);
    setFormData({
      title: promotion.title,
      description: promotion.description,
      imageUrl: promotion.imageUrl || '',
      startDate: new Date(promotion.startDate),
      endDate: new Date(promotion.endDate),
      status: promotion.status,
      scope: promotion.scope,
      discountType: promotion.discountType,
      discountValue: {
        value: promotion.discountValue.value,
        maxDiscount: promotion.discountValue.maxDiscount
      },
      conditions: {
        productIds: promotion.conditions.productIds || [],
        categoryIds: promotion.conditions.categoryIds || [],
        minOrderAmount: promotion.conditions.minOrderAmount,
        quantityConditions: promotion.conditions.quantityConditions || []
      },
      buyXGetYDetails: promotion.buyXGetYDetails || {
        buyQuantity: 1,
        getQuantity: 1
      },
      bundleDetails: promotion.bundleDetails || {
        productIds: [],
        bundlePrice: 0
      },
      usageLimit: promotion.usageLimit,
      priority: promotion.priority,
      isActive: promotion.isActive,
      showInOffers: promotion.showInOffers,
      selectedProductIds: promotion.conditions.productIds || [],
      selectedCategoryIds: promotion.conditions.categoryIds || [],
      quantityConditions: promotion.conditions.quantityConditions || []
    });
    setErrors({});
    setTabValue(0);
    setOpenDialog(true);
  };

  // فتح نافذة حذف عرض
  const handleDeleteClick = (promotion: Promotion) => {
    setCurrentPromotion(promotion);
    setOpenDeleteDialog(true);
  };

  // حذف عرض
  const handleDeleteConfirm = async () => {
    if (!currentPromotion) return;

    try {
      await deletePromotion(currentPromotion.id);
      setPromotions(promotions.filter(p => p.id !== currentPromotion.id));
      setSnackbar({
        open: true,
        message: 'تم حذف العرض بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting promotion:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حذف العرض',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
      setCurrentPromotion(null);
    }
  };

  // تغيير حالة نشاط العرض
  const handleToggleActive = async (promotion: Promotion) => {
    try {
      await updatePromotion(promotion.id, { isActive: !promotion.isActive });
      setPromotions(promotions.map(p => p.id === promotion.id ? { ...p, isActive: !p.isActive } : p));
      setSnackbar({
        open: true,
        message: `تم ${!promotion.isActive ? 'تفعيل' : 'إلغاء تفعيل'} العرض بنجاح`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating promotion status:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحديث حالة العرض',
        severity: 'error'
      });
    }
  };

  // حفظ العرض (إضافة أو تعديل)
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      // تحضير بيانات العرض
      const promotionData: CreatePromotionData | UpdatePromotionData = {
        title: formData.title,
        description: formData.description,
        imageUrl: formData.imageUrl,
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status,
        scope: formData.scope,
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        conditions: {
          productIds: formData.scope === PromotionScope.PRODUCT ? formData.selectedProductIds : undefined,
          categoryIds: formData.scope === PromotionScope.CATEGORY ? formData.selectedCategoryIds : undefined,
          minOrderAmount: formData.conditions.minOrderAmount,
          quantityConditions: formData.quantityConditions.length > 0 ? formData.quantityConditions : undefined
        },
        buyXGetYDetails: formData.discountType === DiscountType.BUY_X_GET_Y ? formData.buyXGetYDetails : undefined,
        bundleDetails: formData.discountType === DiscountType.BUNDLE ? formData.bundleDetails : undefined,
        usageLimit: formData.usageLimit,
        priority: formData.priority,
        isActive: formData.isActive,
        showInOffers: formData.showInOffers
      };

      if (currentPromotion) {
        // تعديل عرض موجود
        await updatePromotion(currentPromotion.id, promotionData);
        setPromotions(promotions.map(p => p.id === currentPromotion.id ? { ...p, ...promotionData, updatedAt: new Date() } : p));
        setSnackbar({
          open: true,
          message: 'تم تحديث العرض بنجاح',
          severity: 'success'
        });
      } else {
        // إضافة عرض جديد
        const newPromotionId = await createPromotion(promotionData as CreatePromotionData);
        const newPromotion: Promotion = {
          id: newPromotionId,
          ...promotionData as CreatePromotionData,
          usageCount: 0,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        setPromotions([...promotions, newPromotion]);
        setSnackbar({
          open: true,
          message: 'تم إنشاء العرض بنجاح',
          severity: 'success'
        });
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving promotion:', error);
      setSnackbar({
        open: true,
        message: `حدث خطأ أثناء ${currentPromotion ? 'تحديث' : 'إنشاء'} العرض`,
        severity: 'error'
      });
    }
  };

  // تغيير قيمة حقل في النموذج
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // تغيير قيمة الخيارات (Switch)
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  // تغيير قيمة التاريخ
  const handleDateChange = (name: string, date: Date | null) => {
    if (date) {
      setFormData({ ...formData, [name]: date });
    }
  };

  // تغيير قيمة قائمة منسدلة
  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({ ...formData, [name]: value });
    }
  };

  // تغيير قيمة التخفيض
  const handleDiscountValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value);

    if (name === 'discountValue') {
      setFormData({
        ...formData,
        discountValue: {
          ...formData.discountValue,
          value: isNaN(numValue) ? 0 : numValue
        }
      });
    } else if (name === 'maxDiscount') {
      setFormData({
        ...formData,
        discountValue: {
          ...formData.discountValue,
          maxDiscount: isNaN(numValue) ? undefined : numValue
        }
      });
    }
  };

  // تغيير المنتجات المحددة
  const handleProductsChange = (_: React.SyntheticEvent, newValue: InventoryItem[]) => {
    const selectedIds = newValue.map(product => product.id);
    setFormData({
      ...formData,
      selectedProductIds: selectedIds,
      conditions: {
        ...formData.conditions,
        productIds: selectedIds
      }
    });
  };

  // تغيير الفئات المحددة
  const handleCategoriesChange = (_: React.SyntheticEvent, newValue: Category[]) => {
    const selectedIds = newValue.map(category => category.id);
    setFormData({
      ...formData,
      selectedCategoryIds: selectedIds,
      conditions: {
        ...formData.conditions,
        categoryIds: selectedIds
      }
    });
  };

  // تغيير تفاصيل عرض اشتر X واحصل على Y
  const handleBuyXGetYChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseInt(value);

    if (name === 'buyQuantity') {
      setFormData({
        ...formData,
        buyXGetYDetails: {
          buyQuantity: isNaN(numValue) ? 1 : numValue,
          getQuantity: formData.buyXGetYDetails?.getQuantity || 1,
          getProductId: formData.buyXGetYDetails?.getProductId
        }
      });
    } else if (name === 'getQuantity') {
      setFormData({
        ...formData,
        buyXGetYDetails: {
          buyQuantity: formData.buyXGetYDetails?.buyQuantity || 1,
          getQuantity: isNaN(numValue) ? 1 : numValue,
          getProductId: formData.buyXGetYDetails?.getProductId
        }
      });
    }
  };

  // تغيير تفاصيل عرض الحزمة
  const handleBundleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value);

    if (name === 'bundlePrice') {
      setFormData({
        ...formData,
        bundleDetails: {
          productIds: formData.bundleDetails?.productIds || [],
          bundlePrice: isNaN(numValue) ? 0 : numValue
        }
      });
    }
  };

  // إضافة شرط كمية
  const handleAddQuantityCondition = () => {
    const newCondition: QuantityCondition = {
      minQuantity: 1,
      maxQuantity: undefined
    };

    setFormData({
      ...formData,
      quantityConditions: [...formData.quantityConditions, newCondition]
    });
  };

  // تغيير شرط كمية
  const handleQuantityConditionChange = (index: number, field: keyof QuantityCondition, value: string) => {
    const numValue = parseInt(value);
    const updatedConditions = [...formData.quantityConditions];

    if (field === 'minQuantity') {
      updatedConditions[index].minQuantity = isNaN(numValue) ? 1 : numValue;
    } else if (field === 'maxQuantity') {
      updatedConditions[index].maxQuantity = isNaN(numValue) || numValue <= 0 ? undefined : numValue;
    }

    setFormData({
      ...formData,
      quantityConditions: updatedConditions
    });
  };

  // حذف شرط كمية
  const handleRemoveQuantityCondition = (index: number) => {
    const updatedConditions = formData.quantityConditions.filter((_, i) => i !== index);

    setFormData({
      ...formData,
      quantityConditions: updatedConditions
    });
  };

  // إغلاق رسالة الإشعار
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // تنسيق نوع التخفيض
  const formatDiscountType = (promotion: Promotion) => {
    switch (promotion.discountType) {
      case DiscountType.PERCENTAGE:
        return `خصم ${promotion.discountValue.value}%`;
      case DiscountType.FIXED_AMOUNT:
        return `خصم ${promotion.discountValue.value} ج.م`;
      case DiscountType.BUY_X_GET_Y:
        if (promotion.buyXGetYDetails) {
          return `اشتر ${promotion.buyXGetYDetails.buyQuantity} واحصل على ${promotion.buyXGetYDetails.getQuantity} مجاناً`;
        }
        return 'عرض خاص';
      case DiscountType.BUNDLE:
        return 'عرض حزمة';
      default:
        return 'عرض خاص';
    }
  };

  // تنسيق نطاق التطبيق
  const formatScope = (promotion: Promotion) => {
    switch (promotion.scope) {
      case PromotionScope.PRODUCT:
        return 'منتجات محددة';
      case PromotionScope.CATEGORY:
        return 'فئات محددة';
      case PromotionScope.ALL:
        return 'جميع المنتجات';
      default:
        return 'غير محدد';
    }
  };

  // تنسيق حالة العرض
  const formatStatus = (status: PromotionStatus) => {
    switch (status) {
      case PromotionStatus.ACTIVE:
        return 'نشط';
      case PromotionStatus.SCHEDULED:
        return 'مجدول';
      case PromotionStatus.EXPIRED:
        return 'منتهي';
      case PromotionStatus.DRAFT:
        return 'مسودة';
      default:
        return 'غير معروف';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Grid container spacing={2} alignItems="center" mb={4}>
          <Grid item xs>
            <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <LocalOfferIcon sx={{ mr: 1 }} />
              إدارة العروض والتخفيضات
            </Typography>
            <Typography variant="body1" color="text.secondary">
              إدارة العروض والتخفيضات على المنتجات في المتجر الإلكتروني
            </Typography>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<AutoFixHighIcon />}
                onClick={() => setShowQuickFix(true)}
                size="small"
              >
                إصلاح سريع
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<BugReportIcon />}
                onClick={() => setShowDiagnostics(true)}
                size="small"
              >
                تشخيص متقدم
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddPromotion}
              >
                إضافة عرض جديد
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : promotions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                لا توجد عروض حالياً
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                قد تكون هناك مشكلة في تحميل العروض أو لم يتم إنشاء أي عروض بعد
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<AutoFixHighIcon />}
                  onClick={() => setShowQuickFix(true)}
                >
                  تشخيص وإصلاح
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddPromotion}
                >
                  إضافة عرض جديد
                </Button>
              </Box>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>العنوان</TableCell>
                    <TableCell>نوع التخفيض</TableCell>
                    <TableCell>نطاق التطبيق</TableCell>
                    <TableCell>تاريخ البدء</TableCell>
                    <TableCell>تاريخ الانتهاء</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>الأولوية</TableCell>
                    <TableCell align="center">الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {promotions.map((promotion) => (
                    <TableRow key={promotion.id}>
                      <TableCell>{promotion.title}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatDiscountType(promotion)}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatScope(promotion)}</TableCell>
                      <TableCell>
                        {new Date(promotion.startDate).toLocaleDateString('ar-EG')}
                      </TableCell>
                      <TableCell>
                        {new Date(promotion.endDate).toLocaleDateString('ar-EG')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatStatus(promotion.status)}
                          color={promotion.status === PromotionStatus.ACTIVE ? 'success' :
                                 promotion.status === PromotionStatus.SCHEDULED ? 'info' :
                                 promotion.status === PromotionStatus.EXPIRED ? 'error' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{promotion.priority}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title={promotion.isActive ? 'إلغاء التفعيل' : 'تفعيل'}>
                            <IconButton
                              color={promotion.isActive ? 'warning' : 'success'}
                              onClick={() => handleToggleActive(promotion)}
                            >
                              {promotion.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="تعديل">
                            <IconButton
                              color="primary"
                              onClick={() => handleEditPromotion(promotion)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="حذف">
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(promotion)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* نافذة إضافة/تعديل عرض */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentPromotion ? 'تعديل عرض' : 'إضافة عرض جديد'}
        </DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="promotion tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="المعلومات الأساسية" id="promotion-tab-0" />
            <Tab label="التخفيض" id="promotion-tab-1" />
            <Tab label="نطاق التطبيق" id="promotion-tab-2" />
            <Tab label="شروط إضافية" id="promotion-tab-3" />
          </Tabs>

          {/* المعلومات الأساسية */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="title"
                  label="عنوان العرض"
                  value={formData.title}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  error={!!errors.title}
                  helperText={errors.title}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="imageUrl"
                  label="رابط الصورة"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  helperText="رابط صورة العرض (اختياري)"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ImageIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="وصف العرض"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="تاريخ البدء"
                  type="date"
                  value={typeof formData.startDate === 'string'
                    ? new Date(formData.startDate).toISOString().split('T')[0]
                    : formData.startDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateChange('startDate', new Date(e.target.value))}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="تاريخ الانتهاء"
                  type="date"
                  value={typeof formData.endDate === 'string'
                    ? new Date(formData.endDate).toISOString().split('T')[0]
                    : formData.endDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateChange('endDate', new Date(e.target.value))}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>حالة العرض</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleSelectChange}
                    label="حالة العرض"
                  >
                    <MenuItem value={PromotionStatus.ACTIVE}>نشط</MenuItem>
                    <MenuItem value={PromotionStatus.SCHEDULED}>مجدول</MenuItem>
                    <MenuItem value={PromotionStatus.DRAFT}>مسودة</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="priority"
                  label="الأولوية"
                  type="number"
                  value={formData.priority}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  helperText="الأولوية الأعلى تطبق أولاً في حالة تداخل العروض"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleSwitchChange}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label="العرض نشط"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showInOffers}
                      onChange={handleSwitchChange}
                      name="showInOffers"
                      color="primary"
                    />
                  }
                  label="إظهار في صفحة العروض"
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* التخفيض */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>نوع التخفيض</InputLabel>
                  <Select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleSelectChange}
                    label="نوع التخفيض"
                  >
                    <MenuItem value={DiscountType.PERCENTAGE}>نسبة مئوية</MenuItem>
                    <MenuItem value={DiscountType.FIXED_AMOUNT}>مبلغ ثابت</MenuItem>
                    <MenuItem value={DiscountType.BUY_X_GET_Y}>اشتر X واحصل على Y</MenuItem>
                    <MenuItem value={DiscountType.BUNDLE}>حزمة منتجات</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {formData.discountType === DiscountType.PERCENTAGE && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="discountValue"
                      label="نسبة التخفيض (%)"
                      type="number"
                      value={formData.discountValue.value}
                      onChange={handleDiscountValueChange}
                      fullWidth
                      margin="normal"
                      error={!!errors.discountValue}
                      helperText={errors.discountValue}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="maxDiscount"
                      label="الحد الأقصى للتخفيض"
                      type="number"
                      value={formData.discountValue.maxDiscount || ''}
                      onChange={handleDiscountValueChange}
                      fullWidth
                      margin="normal"
                      helperText="الحد الأقصى للتخفيض (اختياري)"
                    />
                  </Grid>
                </>
              )}

              {formData.discountType === DiscountType.FIXED_AMOUNT && (
                <Grid item xs={12}>
                  <TextField
                    name="discountValue"
                    label="قيمة التخفيض"
                    type="number"
                    value={formData.discountValue.value}
                    onChange={handleDiscountValueChange}
                    fullWidth
                    margin="normal"
                    error={!!errors.discountValue}
                    helperText={errors.discountValue}
                    required
                  />
                </Grid>
              )}

              {formData.discountType === DiscountType.BUY_X_GET_Y && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="buyQuantity"
                      label="كمية الشراء"
                      type="number"
                      value={formData.buyXGetYDetails?.buyQuantity || 1}
                      onChange={handleBuyXGetYChange}
                      fullWidth
                      margin="normal"
                      error={!!errors.buyQuantity}
                      helperText={errors.buyQuantity}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="getQuantity"
                      label="كمية الهدية"
                      type="number"
                      value={formData.buyXGetYDetails?.getQuantity || 1}
                      onChange={handleBuyXGetYChange}
                      fullWidth
                      margin="normal"
                      error={!!errors.getQuantity}
                      helperText={errors.getQuantity}
                      required
                    />
                  </Grid>
                </>
              )}

              {formData.discountType === DiscountType.BUNDLE && (
                <>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={products}
                      getOptionLabel={(option) => option.name}
                      value={products.filter(product => formData.bundleDetails?.productIds.includes(product.id))}
                      onChange={(_, newValue) => {
                        const selectedIds = newValue.map(product => product.id);
                        setFormData({
                          ...formData,
                          bundleDetails: {
                            productIds: selectedIds,
                            bundlePrice: formData.bundleDetails?.bundlePrice || 0
                          }
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="منتجات الحزمة"
                          margin="normal"
                          error={!!errors.bundleProductIds}
                          helperText={errors.bundleProductIds || 'اختر منتجين على الأقل للحزمة'}
                          required
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="bundlePrice"
                      label="سعر الحزمة"
                      type="number"
                      value={formData.bundleDetails?.bundlePrice || 0}
                      onChange={handleBundleChange}
                      fullWidth
                      margin="normal"
                      error={!!errors.bundlePrice}
                      helperText={errors.bundlePrice}
                      required
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </TabPanel>

          {/* نطاق التطبيق */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>نطاق تطبيق العرض</InputLabel>
                  <Select
                    name="scope"
                    value={formData.scope}
                    onChange={handleSelectChange}
                    label="نطاق تطبيق العرض"
                  >
                    <MenuItem value={PromotionScope.PRODUCT}>منتجات محددة</MenuItem>
                    <MenuItem value={PromotionScope.CATEGORY}>فئات محددة</MenuItem>
                    <MenuItem value={PromotionScope.ALL}>جميع المنتجات</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {formData.scope === PromotionScope.PRODUCT && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={products}
                    getOptionLabel={(option) => option.name}
                    value={products.filter(product => formData.selectedProductIds.includes(product.id))}
                    onChange={handleProductsChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="المنتجات المشمولة في العرض"
                        margin="normal"
                        error={!!errors.selectedProductIds}
                        helperText={errors.selectedProductIds}
                        required
                      />
                    )}
                  />
                </Grid>
              )}

              {formData.scope === PromotionScope.CATEGORY && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={categories}
                    getOptionLabel={(option) => option.name}
                    value={categories.filter(category => formData.selectedCategoryIds.includes(category.id))}
                    onChange={handleCategoriesChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="الفئات المشمولة في العرض"
                        margin="normal"
                        error={!!errors.selectedCategoryIds}
                        helperText={errors.selectedCategoryIds}
                        required
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* شروط إضافية */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="usageLimit"
                  label="الحد الأقصى لاستخدام العرض"
                  type="number"
                  value={formData.usageLimit || ''}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  helperText="الحد الأقصى لعدد مرات استخدام العرض (اختياري)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="minOrderAmount"
                  label="الحد الأدنى لمبلغ الطلب"
                  type="number"
                  value={formData.conditions.minOrderAmount || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = parseFloat(value);
                    setFormData({
                      ...formData,
                      conditions: {
                        ...formData.conditions,
                        minOrderAmount: isNaN(numValue) ? undefined : numValue
                      }
                    });
                  }}
                  fullWidth
                  margin="normal"
                  helperText="الحد الأدنى لمبلغ الطلب لتطبيق العرض (اختياري)"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">شروط الكمية</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddQuantityCondition}
                  >
                    إضافة شرط كمية
                  </Button>
                </Box>

                {formData.quantityConditions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    لا توجد شروط كمية. يمكنك إضافة شروط للكمية لتطبيق العرض على كميات محددة من المنتج.
                  </Typography>
                ) : (
                  formData.quantityConditions.map((condition, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                      }}
                    >
                      <TextField
                        label="الحد الأدنى للكمية"
                        type="number"
                        value={condition.minQuantity}
                        onChange={(e) => handleQuantityConditionChange(index, 'minQuantity', e.target.value)}
                        size="small"
                        sx={{ flexGrow: 1 }}
                      />
                      <TextField
                        label="الحد الأقصى للكمية"
                        type="number"
                        value={condition.maxQuantity || ''}
                        onChange={(e) => handleQuantityConditionChange(index, 'maxQuantity', e.target.value)}
                        size="small"
                        sx={{ flexGrow: 1 }}
                        helperText="اتركه فارغاً لعدم وجود حد أقصى"
                      />
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveQuantityCondition(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))
                )}
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة تأكيد الحذف */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من حذف عرض "{currentPromotion?.title}"؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* رسالة الإشعار */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* مكون تشخيص النظام */}
      <Dialog
        open={showDiagnostics}
        onClose={() => setShowDiagnostics(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">
              🔍 تشخيص نظام العروض والتخفيضات
            </Typography>
            <IconButton onClick={() => setShowDiagnostics(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <PromotionDiagnosticsComponent />
        </DialogContent>
      </Dialog>

      {/* مكون الإصلاح السريع */}
      <Dialog
        open={showQuickFix}
        onClose={() => setShowQuickFix(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">
              🚀 إصلاح سريع للعروض
            </Typography>
            <IconButton onClick={() => setShowQuickFix(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <PromotionQuickFix
            onComplete={() => {
              setShowQuickFix(false);
              loadPromotions(); // إعادة تحميل العروض بعد الإصلاح
            }}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default PromotionsManagementPage;
